import { makeObservable, observable, action, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { callLogsApi, CallLog, CallStatus, SaveOutcomePayload } from '../api/callLogsApi'
import { queryClient } from '../queryClient'
import { authStore } from './authStore'
import { signalRService, type CallEndedEvent } from '../services/signalRService'

export interface CallTarget {
  entityType: 'LEAD' | 'CONTACT' | 'DEAL' | 'RETENTION' | 'SALES'
  entityId: string
  name: string
  phone: string
  companyName?: string
  designation?: string
}

export type CallPhase = 'IDLE' | 'PLACE_CALL' | 'CALLING' | 'OUTCOME' | 'INCOMING'

export class CallStore {
  phase: CallPhase = 'IDLE'
  activeCall: CallLog | null = null
  target: CallTarget | null = null

  /** CRM call_logs.id for the call currently in progress — used to match SignalR events. */
  currentCallId: string | null = null

  /**
   * True when the OUTCOME modal was triggered automatically by a SignalR CallEnded event.
   * When true the status dropdown is pre-filled from the webhook and shown as read-only.
   */
  statusLockedByWebhook = false

  isMinimized = false
  isMuted = false
  isSpeakerOn = false

  isStarting = false
  isEnding = false
  isSavingOutcome = false

  elapsedSeconds = 0
  private timerId: ReturnType<typeof setInterval> | null = null
  private unsubscribeCallEnded: (() => void) | null = null

  constructor() {
    makeObservable(this, {
      phase:                 observable,
      activeCall:            observable,
      target:                observable,
      currentCallId:         observable,
      statusLockedByWebhook: observable,
      isMinimized:           observable,
      isMuted:               observable,
      isSpeakerOn:           observable,
      isStarting:            observable,
      isEnding:              observable,
      isSavingOutcome:       observable,
      elapsedSeconds:        observable,

      showPlaceCall:         action,
      cancelPlaceCall:       action,
      startOutgoingCall:     action,
      receiveIncomingCall:   action,
      acceptIncomingCall:    action,
      rejectIncomingCall:    action,
      endActiveCall:         action,
      saveCallOutcome:       action,
      handleCallEnded:       action,
      invalidateCallQueries: action,
      setMinimized:          action,
      toggleMute:            action,
      toggleSpeaker:         action,
    })
    // Register the SignalR CallEnded handler once at construction time.
    // The unsubscribe function is stored so we can clean up if needed.
    this.unsubscribeCallEnded = signalRService.onCallEnded(
      action((event: CallEndedEvent) => this.handleCallEnded(event))
    )
  }

  showPlaceCall(target: CallTarget) {
    this.target = target
    this.phase = 'PLACE_CALL'
    this.isMinimized = false
    this.isMuted = false
    this.isSpeakerOn = false
    this.activeCall = null
    this.elapsedSeconds = 0
    this.stopTimer()
  }

  cancelPlaceCall() {
    this.phase = 'IDLE'
    this.target = null
    this.activeCall = null
    this.currentCallId = null
  }

  async startOutgoingCall() {
    if (!this.target) return

    // ── Customer phone guard ──────────────────────────────────────────────────
    const phone = this.target.phone?.trim()
    if (!phone || phone.replace(/\D/g, '').length < 7) {
      toast.error('Customer phone number is missing or invalid.')
      return
    }

    this.isStarting = true

    // C-Zentrix Agent ID validation check is disabled for Knowlarity.
    // const czentrixAgentId: number | undefined = undefined;
    /*
    if (!czentrixAgentId) {
      toast.warn(
        'C-Zentrix Agent ID not configured for your account. ' +
        'Ask an admin to set it in Users settings. The call log will be created but the phone will not ring.',
        { autoClose: 7000 }
      )
    }
    */

    const dialPayload = {
      callType:        'OUTGOING' as const,
      phoneNumber:     this.target.phone,
    }

    try {
      let call: CallLog
      if (this.target.entityType === 'LEAD') {
        call = await callLogsApi.startCall(this.target.entityId, dialPayload)
      } else if (this.target.entityType === 'DEAL') {
        call = await callLogsApi.startCallForDeal(this.target.entityId, dialPayload)
      } else if (this.target.entityType === 'RETENTION') {
        call = await callLogsApi.startCallForRetention(this.target.entityId, dialPayload)
      } else if (this.target.entityType === 'SALES') {
        call = await callLogsApi.startCallForSales(this.target.entityId, dialPayload)
      } else {
        call = await callLogsApi.startCallForContact(this.target.entityId, dialPayload)
      }

      // ── Agent phone not configured: surface error & revert ────────────────
      if (call.status === 'FAILED') {
        toast.error(
          (call as any).notes ||
          'Call failed. Your phone number is not configured. Ask admin to set it in your profile.',
          { autoClose: 8000 }
        )
        runInAction(() => {
          this.phase = 'IDLE'
          this.activeCall = null
          this.isStarting = false
        })
        return
      }

      runInAction(() => {
        this.activeCall = call
        this.currentCallId = call.id   // store for SignalR event matching
        this.phase = 'CALLING'
        this.startTimer()
      })
    } catch (err) {
      toast.error('Failed to start call')
    } finally {
      runInAction(() => {
        this.isStarting = false
      })
    }
  }

  receiveIncomingCall(target: CallTarget) {
    this.target = target
    this.phase = 'INCOMING'
    this.isMinimized = false
    this.isMuted = false
    this.isSpeakerOn = false
    this.elapsedSeconds = 0
    this.stopTimer()
    
    // Create a mock incoming CallLog object for the UI
    this.activeCall = {
      id: 'incoming-' + Date.now(),
      callType: 'INCOMING',
      status: 'INITIATED',
      outcome: null,
      notes: null,
      followUpDate: null,
      startedAt: new Date().toISOString(),
      endedAt: null,
      durationSeconds: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      leadName: target.entityType === 'LEAD' ? target.name : undefined,
      leadPhone: target.entityType === 'LEAD' ? target.phone : undefined,
      contactName: target.entityType !== 'LEAD' ? target.name : undefined,
      contactPhone: target.entityType !== 'LEAD' ? target.phone : undefined,
      initiatedBy: { id: 'incoming', name: 'Incoming Caller', email: '', avatarUrl: null }
    }
  }

  acceptIncomingCall() {
    if (!this.activeCall) return
    runInAction(() => {
      this.phase = 'CALLING'
      this.activeCall!.status = 'CONNECTED'
      this.startTimer()
    })
  }

  rejectIncomingCall() {
    runInAction(() => {
      this.phase = 'IDLE'
      this.activeCall = null
      this.target = null
    })
  }

  async endActiveCall(status: CallStatus = 'CONNECTED') {
    if (!this.activeCall || !this.target) return
    
    // If it's a mock incoming call that was rejected or never accepted
    if (this.activeCall.id.startsWith('incoming-') && this.activeCall.status === 'INITIATED') {
      this.rejectIncomingCall()
      return
    }

    this.isEnding = true
    this.stopTimer()
    try {
      let call: CallLog
      if (this.target.entityType === 'LEAD') {
        call = await callLogsApi.endCall(this.target.entityId, this.activeCall.id, { status })
      } else if (this.target.entityType === 'DEAL') {
        call = await callLogsApi.endCallForDeal(this.target.entityId, this.activeCall.id, { status })
      } else if (this.target.entityType === 'RETENTION') {
        call = await callLogsApi.endCallForRetention(this.target.entityId, this.activeCall.id, { status })
      } else if (this.target.entityType === 'SALES') {
        call = await callLogsApi.endCallForSales(this.target.entityId, this.activeCall.id, { status })
      } else {
        call = await callLogsApi.endCallForContact(this.target.entityId, this.activeCall.id, { status })
      }
      runInAction(() => {
        this.activeCall = call
        this.phase = 'OUTCOME'
        this.isMinimized = false
      })
    } catch (err) {
      toast.error('Failed to end call')
    } finally {
      runInAction(() => {
        this.isEnding = false
      })
    }
  }

  async saveCallOutcome(payload: SaveOutcomePayload) {
    if (!this.activeCall || !this.target) return
    
    this.isSavingOutcome = true
    try {
      if (this.target.entityType === 'LEAD') {
        await callLogsApi.saveOutcome(this.target.entityId, this.activeCall.id, payload)
      } else if (this.target.entityType === 'DEAL') {
        await callLogsApi.saveOutcomeForDeal(this.target.entityId, this.activeCall.id, payload)
      } else if (this.target.entityType === 'RETENTION') {
        await callLogsApi.saveOutcomeForRetention(this.target.entityId, this.activeCall.id, payload)
      } else if (this.target.entityType === 'SALES') {
        await callLogsApi.saveOutcomeForSales(this.target.entityId, this.activeCall.id, payload)
      } else {
        await callLogsApi.saveOutcomeForContact(this.target.entityId, this.activeCall.id, payload)
      }
      runInAction(() => {
        this.phase                 = 'IDLE'
        this.activeCall            = null
        this.target                = null
        this.currentCallId         = null
        this.statusLockedByWebhook = false
      })
      toast.success('Call logged successfully')
      
      // Invalidate queries to refresh lists/timelines
      this.invalidateCallQueries()
    } catch (err) {
      toast.error('Failed to save call outcome')
    } finally {
      runInAction(() => {
        this.isSavingOutcome = false
      })
    }
  }

  /**
   * Called when a SignalR "CallEnded" event arrives from the backend.
   * Only acts if the event matches the currently active call.
   */
  handleCallEnded(event: CallEndedEvent) {
    // Guard: ignore events that don't belong to the current call.
    if (!this.currentCallId || event.callId.toString() !== this.currentCallId) {
      return
    }

    runInAction(() => {
      this.stopTimer()

      // Update the active call record with data from the backend.
      // Never manually override status — use exactly what the backend sent.
      if (this.activeCall) {
        this.activeCall = {
          ...this.activeCall,
          status:          event.status as CallStatus,
          endedAt:         event.endedAt,
          durationSeconds: event.duration,
          recordingUrl:    event.recordingUrl,
          agentCallStatus: event.agentCallStatus,
          customerCallStatus: event.customerCallStatus
        }
      }

      this.currentCallId        = null
      this.statusLockedByWebhook = true   // status is from the webhook — lock the dropdown
      this.phase                = 'OUTCOME'
      this.isMinimized          = false
    })

    // Refresh all relevant data so the UI reflects the completed call.
    this.invalidateCallQueries()
  }

  /** Invalidate all query-cache keys that display call / activity data. */
  invalidateCallQueries() {
    const entityId = this.target?.entityId

    if (this.target?.entityType === 'SALES') {
      queryClient.invalidateQueries({ queryKey: ['sales-record', entityId] })
      queryClient.invalidateQueries({ queryKey: ['sales-activities', entityId] })
      queryClient.invalidateQueries({ queryKey: ['sales-calls', entityId] })
    }

    queryClient.invalidateQueries({ queryKey: ['call-logs'] })
    queryClient.invalidateQueries({ queryKey: ['call-logs-lead'] })
    queryClient.invalidateQueries({ queryKey: ['call-logs-contact'] })
    queryClient.invalidateQueries({ queryKey: ['deal-call-logs'] })
    queryClient.invalidateQueries({ queryKey: ['lead-activities'] })
    queryClient.invalidateQueries({ queryKey: ['lead-notes'] })
    queryClient.invalidateQueries({ queryKey: ['lead'] })
    queryClient.invalidateQueries({ queryKey: ['deal'] })
    queryClient.invalidateQueries({ queryKey: ['deal-timeline'] })
    queryClient.invalidateQueries({ queryKey: ['contact'] })
    queryClient.invalidateQueries({ queryKey: ['contact-activities'] })
    queryClient.invalidateQueries({ queryKey: ['retention-activities'] })
    queryClient.invalidateQueries({ queryKey: ['sales-activities'] })
    queryClient.invalidateQueries({ queryKey: ['sales-calls'] })
  }

  setMinimized(val: boolean) {
    this.isMinimized = val
  }

  toggleMute() {
    this.isMuted = !this.isMuted
  }

  toggleSpeaker() {
    this.isSpeakerOn = !this.isSpeakerOn
  }

  private startTimer() {
    this.stopTimer()
    this.elapsedSeconds = 0
    this.timerId = setInterval(() => {
      runInAction(() => {
        this.elapsedSeconds++
      })
    }, 1000)
  }

  // NOTE: pollCallStatus() removed — polling replaced by manual End Call button.
  // Firebase push notification will be wired here in a future update for auto-detection.

  private stopTimer() {
    if (this.timerId) {
      clearInterval(this.timerId)
      this.timerId = null
    }
  }
}

export const callStore = new CallStore()
